import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { type FormikProps } from 'formik';

import { type PriorAuthFormValues } from 'components/intakeCases/dialogs/LogPriorAuthCommunicationDialog';
import { ActionPlan } from 'services/constants';

const INVALID_FOR_EPA_SUBMISSION_MESSAGE =
    ' The following required fields are missing and must be entered to log an action plan of FMH to submit:';

const getMissingFieldsMessage = (missingEpaFields: string[]) => missingEpaFields;

interface PrescriberCommunicationFormProps {
    formik: FormikProps<PriorAuthFormValues>;
    isPrescriberOutreachComplete: boolean;
    missingEpaFields: string[];
}

export const PrescriberCommunicationForm = ({
    formik,
    isPrescriberOutreachComplete,
    missingEpaFields,
}: PrescriberCommunicationFormProps) => {
    const isValidForEPaSubmission = missingEpaFields.length === 0;
    const isFMHToSubmit =
        formik.values.actionPlan === ActionPlan.FMH_TO_SUBMIT &&
        (isPrescriberOutreachComplete || formik.values.isPrescriberOutreachComplete);

    return (
        <>
            <RadioButtonGroupFormik
                name="communicationDirection"
                label="Communication Direction"
                required
                options={['Inbound', 'Outbound']}
            />
            <RadioButtonGroupFormik
                name="communicationType"
                label="Communication Type"
                required
                options={['Fax', 'Phone']}
            />
            <CheckboxListFormik
                name="isPrescriberOutreachComplete"
                options={['Prescriber Outreach Complete']}
                disabled={isPrescriberOutreachComplete}
                values={
                    formik.values.isPrescriberOutreachComplete
                        ? ['Prescriber Outreach Complete']
                        : []
                }
                onChange={(values) => {
                    void formik.setFieldValue('isPrescriberOutreachComplete', values.length === 1);
                }}
            />
            {(isPrescriberOutreachComplete || formik.values.isPrescriberOutreachComplete) && (
                <div className="margin__bottom--medium">
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label="Are you sure you want to mark prescriber outreach as successful? This action cannot be undone."
                        wrapText
                    />
                </div>
            )}
            {(isPrescriberOutreachComplete || formik.values.isPrescriberOutreachComplete) && (
                <RadioButtonGroupFormik
                    name="actionPlan"
                    label="Action Plan"
                    options={Object.values(ActionPlan)}
                    required
                    onChange={(value) => {
                        void formik.setFieldValue('actionPlan', value);
                    }}
                    selectedValue={formik.values.actionPlan}
                />
            )}
            {!isValidForEPaSubmission && isFMHToSubmit && (
                <div className="margin__bottom--medium">
                    <InlineMessage
                        intent={InlineMessage.Intent.ERROR}
                        label={
                            <>
                                {INVALID_FOR_EPA_SUBMISSION_MESSAGE}
                                <ul className="margin__bottom--small">
                                    {getMissingFieldsMessage(missingEpaFields).map((bullet) => (
                                        <li key={bullet}>{bullet}</li>
                                    ))}
                                </ul>
                            </>
                        }
                        wrapText
                    />
                </div>
            )}
            <DateInputFormik id="priorAuthActivityDate" label="Date" required showTime />
            <TextAreaFormik id="note" label="Note" />
        </>
    );
};
